/**
 * Vocabulary (Lexicon) settings contract — the C15 round-trip surface
 * (Normal Core MASTER-PRD §6.5.9 / §12-6c, GROUNDING-PRD §4.3). The
 * Settings → Workspace → Identity → Vocabulary editor reads the resolved
 * {@link WorkspaceLexicon}, saves a per-workspace override into the
 * `vocab.lexicon` settings key, resets back to the canonical default, and
 * re-derives from the workspace's business description (the offline advisor
 * preset — zero LLM tokens).
 *
 * Owner-gated in the routes (mirrors the security contract). Deliberately NOT
 * MCP tools — agents speak the Lexicon through the core helpers; these are the
 * human-UI affordances. All data flows through this contract — no raw fetch.
 */

import { z } from "zod";
import type { Operation } from "../types";

// ── Lexicon shapes (mirror packages/core/src/vocab.ts WorkspaceLexicon) ──────
// Kept as a zod mirror so the route can validate the inbound override before it
// hits the core `validateLexicon` guard. Loose where the source type is loose
// (every override field is partial; absent ⇒ canonical fallback).

export const lexiconProvenanceSchema = z.object({
  source: z.enum(["preset", "llm", "user"]),
  confidence: z.number().optional(),
  confirmed_at: z.string().optional(),
});

export const lexiconTermSchema = z.object({
  label: z.string(),
  labelOne: z.string().optional(),
  hint: z.string().optional(),
  provenance: lexiconProvenanceSchema.optional(),
});
export type LexiconTermDto = z.infer<typeof lexiconTermSchema>;

/** A stream override = a term plus optional per-stage terms. */
const streamTermSchema = lexiconTermSchema.extend({
  stages: z.record(z.string(), lexiconTermSchema).optional(),
});

export const workspaceLexiconSchema = z.object({
  version: z.literal(1),
  business: z.string().optional(),
  industry: z.string().optional(),
  streams: z.record(z.string(), streamTermSchema),
  commitmentKinds: z.record(z.string(), lexiconTermSchema),
  nouns: z.record(z.string(), lexiconTermSchema),
});
export type WorkspaceLexiconDto = z.infer<typeof workspaceLexiconSchema>;

// ── get ──────────────────────────────────────────────────────────────────────
// The resolved lexicon (workspace override ?? canonical default), a flag for
// whether a custom override is set vs the canonical fallback, and the business
// echo (powers the "re-derive?" staleness affordance) + whether a business
// description exists at all (gates the "Re-derive" button).

const getPayload = z.object({
  /** The lexicon the UI edits — the stored override, or the canonical default. */
  lexicon: workspaceLexiconSchema,
  /** True when a `vocab.lexicon` override is set; false ⇒ canonical fallback. */
  custom: z.boolean(),
  /** The business description the workspace can re-derive from (null ⇒ none). */
  business: z.string().nullable(),
});
export type VocabGetPayload = z.infer<typeof getPayload>;

const getOp: Operation<void, void, void, VocabGetPayload> = {
  method: "GET",
  path: () => "/api/vocab",
  response: getPayload,
};

// ── save ─────────────────────────────────────────────────────────────────────

const saveBody = z.object({ lexicon: workspaceLexiconSchema });
const mutationResult = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
});
export type VocabMutationResult = z.infer<typeof mutationResult>;

const saveOp: Operation<
  void,
  void,
  z.infer<typeof saveBody>,
  VocabMutationResult
> = {
  method: "POST",
  path: () => "/api/vocab/save",
  body: saveBody,
  response: mutationResult,
  // writes vocab.lexicon — get.lexicon + get.custom both move.
  invalidates: [getOp],
};

// ── reset ────────────────────────────────────────────────────────────────────
// Clears the `vocab.lexicon` setting → canonical fallback.

const resetOp: Operation<void, void, void, VocabMutationResult> = {
  method: "POST",
  path: () => "/api/vocab/reset",
  response: mutationResult,
  // clears override → custom:false + canonical lexicon.
  invalidates: [getOp],
};

// ── derive ───────────────────────────────────────────────────────────────────
// Apply the offline industry preset matching the workspace's business
// description (the advisor profile match — zero LLM tokens). Returns the
// derived lexicon so the editor can show it without a refetch race.

const derivePayload = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  lexicon: workspaceLexiconSchema.optional(),
  /** The advisor industry the preset was matched from. */
  industry: z.string().optional(),
});
export type VocabDerivePayload = z.infer<typeof derivePayload>;

const deriveOp: Operation<void, void, void, VocabDerivePayload> = {
  method: "POST",
  path: () => "/api/vocab/derive",
  response: derivePayload,
  // persists a derived lexicon override.
  invalidates: [getOp],
};

export const vocabContract = {
  get: getOp,
  save: saveOp,
  reset: resetOp,
  derive: deriveOp,
};
